import {
  deleteField,
  getDocFromServer,
  runTransaction,
  setDoc,
} from "firebase/firestore";
import type {
  Account,
  AccountSettings,
  Animal,
  Mind,
  OnboardingProgress,
  User,
  WebOnboardingHandoff,
} from "@um/contracts";
import {
  auth,
  db,
  getDocument,
  isOnboardingProgress,
  selectAccountSettingsLoaded,
  useAuthStore,
  waitForState,
} from "@um/ui";
import {
  acknowledgeWebHandoff,
  clearWebHandoffIntent,
  hasHandledWebHandoff,
  isWebHandoff,
  webHandoffId,
  accountWebHandoff,
  webHandoffIsComplete,
} from "./webOnboardingSession";
import {
  ensureWebHandoffVersion,
  webHandoffVersion,
} from "./webOnboardingRelease";

export const WEB_HANDOFF_TIMEOUT_MS = 8_000;
const RETRY_DELAYS_MS = [250, 750];
type Handoff = WebOnboardingHandoff;
export type HandoffResult =
  | { status: "none" | "deferred" | "discovery-unavailable" }
  | { status: "resumed"; destination: "/" | "/onboarding" }
  | {
      status: "retry" | "update-required" | "recovery-required";
      accountId?: string;
    };

function sameSession(a: Handoff | undefined, b: Handoff): boolean {
  return !!a && webHandoffId(a) === webHandoffId(b);
}
function matchesSetup(
  handoff: Handoff,
  progress: OnboardingProgress | undefined
): boolean {
  return (
    isOnboardingProgress(progress) &&
    progress?.flowType !== "onboarding" &&
    Number.isInteger(progress?.stepIndex) &&
    progress!.stepIndex >= 0 &&
    progress?.collectedData?.flowAccountId === handoff.accountId &&
    progress.collectedData.characterId === handoff.characterId &&
    progress.collectedData.webConversionPending === true
  );
}
function isWebCheckpoint(
  accountId: string,
  progress: OnboardingProgress
): boolean {
  return (
    progress.flowType === "post-character-creation" &&
    progress.collectedData.platformExperience === "web" &&
    matchesSetup(
      {
        accountId,
        characterId: String(progress.collectedData.characterId || ""),
        status: "pending",
      },
      progress
    )
  );
}

/** The session uses the character's immutable server creation time, not the tab's clock. */
export async function saveOnboardingProgress(
  accountId: string,
  progress: OnboardingProgress
): Promise<Handoff | null> {
  const webCheckpoint = isWebCheckpoint(accountId, progress);
  if (webCheckpoint && useAuthStore.getState().isHandlingCredentialCollision) {
    await waitForState(
      useAuthStore,
      (state) => !state.isHandlingCredentialCollision,
      15_000
    );
  }
  const settingsRef = getDocument<AccountSettings>(
    `Account/${accountId}/Settings/account`
  );
  const user = auth.currentUser;
  if (!user || user.isAnonymous || !webCheckpoint) {
    await setDoc(
      settingsRef,
      { onboardingProgress: progress },
      { merge: true }
    );
    return null;
  }
  const candidate: Handoff = {
    accountId,
    characterId: String(progress.collectedData.characterId),
    status: "pending",
  };
  if (!isWebHandoff(candidate)) throw new Error("Invalid web setup");
  const userRef = getDocument<User>(`User/${user.uid}`);
  const minimumAppVersion = await webHandoffVersion();

  const saved = await runTransaction(db, async (transaction) => {
    const userData = (await transaction.get(userRef)).data();
    const settings = (await transaction.get(settingsRef)).data();
    const previous = userData?.webOnboardingHandoff;
    const stored = settings?.webOnboardingHandoff;
    // Keep origin on the account too. A later handoff on another account must
    // not let an old browser tab overwrite a setup already resumed in the app.
    if (
      isWebHandoff(stored) &&
      sameSession(stored, candidate) &&
      stored.status !== "pending"
    )
      return stored!;
    if (
      isWebHandoff(previous) &&
      sameSession(previous, candidate) &&
      previous.status !== "pending"
    ) {
      // Upgrade legacy User-only markers so this explicit link keeps working
      // even after another account becomes the user's latest web session.
      transaction.set(
        settingsRef,
        { webOnboardingHandoff: previous },
        { merge: true }
      );
      return previous;
    }
    const animal = (
      await transaction.get(
        getDocument<Animal>(
          `Account/${accountId}/Animal/${candidate.characterId}`
        )
      )
    ).data();
    const createdAtMillis = animal?.created?.toMillis();
    if (!createdAtMillis || !Number.isFinite(createdAtMillis))
      throw new Error("Unable to verify web setup");
    let previousCreated = previous?.createdAtMillis || 0;
    if (
      isWebHandoff(previous) &&
      !sameSession(previous, candidate) &&
      !previousCreated
    ) {
      const previousAnimal = (
        await transaction.get(
          getDocument<Animal>(
            `Account/${previous.accountId}/Animal/${previous.characterId}`
          )
        )
      ).data();
      previousCreated = previousAnimal?.created?.toMillis() || 0;
    }
    const handoff: Handoff = {
      ...candidate,
      sessionId: webHandoffId(candidate),
      userId: user.uid,
      createdAtMillis,
      completionVersion: 1,
      ...(minimumAppVersion ? { minimumAppVersion } : {}),
    };
    // Also protect progress written by an older native client without session metadata.
    if (auth.currentUser?.uid !== user.uid)
      throw new Error("Sign-in changed while saving setup");
    const legacyMarker = sameSession(stored, candidate)
      ? stored
      : sameSession(previous, candidate)
        ? previous
        : undefined;
    const protectedStatus =
      legacyMarker &&
      !legacyMarker.completionVersion &&
      settings?.createdDefaultCharacter &&
      !settings.onboardingProgress
        ? "complete"
        : matchesSetup(handoff, settings?.onboardingProgress) &&
            settings?.onboardingProgress?.collectedData.platformExperience !==
              "web"
          ? "resumed"
          : undefined;
    if (protectedStatus) {
      const protectedHandoff: Handoff = { ...handoff, status: protectedStatus };
      // An older app may still be finishing this session. Do not require a
      // completion write that the app which resumed it does not know to make.
      if (protectedStatus === "resumed" && !legacyMarker?.completionVersion)
        delete protectedHandoff.completionVersion;
      transaction.set(
        settingsRef,
        { webOnboardingHandoff: protectedHandoff },
        { merge: true }
      );
      if (sameSession(previous, handoff))
        transaction.update(userRef, { webOnboardingHandoff: protectedHandoff });
      return protectedHandoff;
    }
    transaction.set(
      settingsRef,
      { onboardingProgress: progress, webOnboardingHandoff: handoff },
      { merge: true }
    );
    const newer =
      !isWebHandoff(previous) ||
      sameSession(previous, handoff) ||
      createdAtMillis > previousCreated ||
      (createdAtMillis === previousCreated &&
        webHandoffId(handoff) > webHandoffId(previous));
    if (newer) transaction.update(userRef, { webOnboardingHandoff: handoff });
    return handoff;
  });
  return saved;
}

export function pendingWebHandoff(): Handoff | undefined {
  const state = useAuthStore.getState();
  const handoff = state.userData?.webOnboardingHandoff;
  const uid = state.user?.uid;
  if (!uid || state.user?.isAnonymous) return;
  const current = selectAccountSettingsLoaded(state)
    ? accountWebHandoff(state.accountId, uid, state.accountSettings, handoff)
    : undefined;
  if (current && !webHandoffIsComplete(current, state.accountSettings))
    return current;
  return unhandledWebHandoff(uid, handoff);
}

function unhandledWebHandoff(
  uid: string,
  handoff: Handoff | undefined,
  continuingAccountId?: string | null
) {
  if (
    !isWebHandoff(handoff) ||
    handoff.status === "complete" ||
    (handoff.userId && handoff.userId !== uid) ||
    (hasHandledWebHandoff(uid, handoff) &&
      handoff.accountId !== continuingAccountId)
  )
    return;
  return handoff;
}

/** Preserve a deliberate account choice on this device, without consuming other devices' continuation. */
export function keepCurrentAccountForWebHandoff(): void {
  const handoff = pendingWebHandoff();
  const state = useAuthStore.getState();
  const uid = state.user?.uid;
  if (uid && handoff) acknowledgeWebHandoff(uid, handoff);
  // The current unfinished account and latest web session can differ. A
  // deliberate account switch must not be undone by either on this device.
  if (uid) {
    const latest = unhandledWebHandoff(uid, state.userData?.webOnboardingHandoff);
    if (latest) acknowledgeWebHandoff(uid, latest);
  }
  clearWebHandoffIntent();
}

class CancelledHandoff extends Error {}

/** Only the resumed web flow uses this; ordinary onboarding keeps its existing completion path. */
export async function completeWebOnboardingHandoff(
  accountId: string,
  data: Record<string, unknown>
): Promise<void> {
  const user = auth.currentUser;
  let active = true;
  const check = () => {
    if (
      !active ||
      !user ||
      user.isAnonymous ||
      auth.currentUser?.uid !== user.uid ||
      useAuthStore.getState().accountId !== accountId
    )
      throw new Error("Your account changed. Please reopen your saved setup.");
  };
  check();
  const settingsRef = getDocument<AccountSettings>(
    `Account/${accountId}/Settings/account`
  );
  const userRef = getDocument<User>(`User/${user!.uid}`);
  const operation = runTransaction(db, async (transaction) => {
    const settings = (await transaction.get(settingsRef)).data();
    const userData = (await transaction.get(userRef)).data();
    const handoff = accountWebHandoff(
      accountId,
      user!.uid,
      settings,
      userData?.webOnboardingHandoff
    );
    if (
      !handoff ||
      data.characterId !== handoff.characterId ||
      data.flowAccountId !== accountId ||
      data.webConversionPending !== true
    )
      throw new Error(
        "We couldn’t verify your saved web setup. Please try again."
      );
    if (handoff.status === "complete") return;
    if (!matchesSetup(handoff, settings?.onboardingProgress))
      throw new Error(
        "Your saved setup changed. Please reopen it before continuing."
      );

    const account = (
      await transaction.get(getDocument<Account>(`Account/${accountId}`))
    ).data();
    const character = (
      await transaction.get(
        getDocument<Animal>(
          `Account/${accountId}/Animal/${handoff.characterId}`
        )
      )
    ).data();
    if (!account || !character)
      throw new Error(
        "We couldn’t load your account and character. Please try again."
      );
    // Champions have a separate setup with no personal Mind. Caregivers need
    // the chosen supported profile, not a Mind linked to their own user role.
    if (account.type !== "champion" || data.whoIsThisFor !== "champion") {
      const self = data.whoIsThisFor === "for-me";
      const selectedMindId = self
        ? data.selfMindId
        : data.mindProfileId || data.selectedMindId;
      // A flow response can replace collectedData while profile creation is
      // finishing. Its missing temporary ID must not reject a valid saved
      // profile; the server-side links below still have to match this account.
      const mindId = selectedMindId ?? character.mind?.id;
      if (typeof mindId !== "string" || !/^[A-Za-z0-9_-]{1,128}$/.test(mindId))
        throw new Error(
          "Your profile setup isn’t finished. Go back to finish your profile, then try again."
        );
      const mindPath = `Account/${accountId}/Mind/${mindId}`;
      const mind = (await transaction.get(getDocument<Mind>(mindPath))).data();
      const role = account.userRoles?.find(
        (entry) => entry.agent?.user?.id === user!.uid
      );
      if (
        !mind ||
        mind.isDeleted ||
        character.mind?.path !== mindPath ||
        (self && role?.mind?.path !== mindPath)
      )
        throw new Error(
          "Your profile setup isn’t finished. Go back to finish your profile, then try again."
        );
    }
    check();
    const completed: Handoff = {
      ...handoff,
      completionVersion: 1,
      status: "complete",
    };
    transaction.set(
      settingsRef,
      {
        webOnboardingHandoff: completed,
        watchedIntroVideo: true,
        createdDefaultCharacter: true,
        acknowledgedSupportingPerson: true,
        onboardingProgress: deleteField(),
      },
      { merge: true }
    );
    if (sameSession(userData?.webOnboardingHandoff, handoff))
      transaction.update(userRef, { webOnboardingHandoff: completed });
  });
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      operation,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          active = false;
          reject(
            new Error("We couldn’t finish saving your setup. Please try again.")
          );
        }, WEB_HANDOFF_TIMEOUT_MS);
      }),
    ]);
    check();
  } finally {
    active = false;
    clearTimeout(timer);
  }
}

/** Account selection and version requirements always require a server-validated handoff. */
export async function resumeWebOnboardingHandoff(
  options: {
    intent?: Handoff;
    /** Unfinished native startup must not treat a missing local marker as a server answer. */
    discover?: boolean;
    isAllowed?: () => boolean;
    signal?: AbortSignal;
  } = {}
): Promise<HandoffResult> {
  const initial = useAuthStore.getState();
  const initialAccountId = initial.accountId;
  const user = auth.currentUser;
  let candidate = options.intent || pendingWebHandoff();
  if (
    !user ||
    user.isAnonymous ||
    initial.isHandlingCredentialCollision ||
    (!isWebHandoff(candidate) && !options.discover)
  )
    return { status: "none" };

  let closed = false;
  let invalidated = false;
  let selected = false;
  let expectedAccount = initialAccountId;
  let stage = "read-user";
  let verifiedAccountId: string | undefined;
  const startedAt = Date.now();
  const isCurrent = () =>
    !closed &&
    !invalidated &&
    !options.signal?.aborted &&
    (options.isAllowed?.() ?? true) &&
    auth.currentUser?.uid === user.uid &&
    !useAuthStore.getState().isHandlingCredentialCollision &&
    useAuthStore.getState().accountId === expectedAccount &&
    (!!options.intent ||
      !candidate ||
      candidate.accountId === initialAccountId ||
      !hasHandledWebHandoff(user.uid, candidate));
  const check = () => {
    if (!isCurrent()) throw new CancelledHandoff();
  };
  const pauseForRetry = async (attempt: number) => {
    console.info("[webOnboardingHandoff] Retrying saved setup", {
      stage,
      attempt: attempt + 2,
    });
    await new Promise((resolve) =>
      setTimeout(resolve, RETRY_DELAYS_MS[attempt])
    );
    check();
  };
  const readFromServer = async <T>(read: () => Promise<T>): Promise<T> => {
    for (let attempt = 0; ; attempt++) {
      check();
      try {
        const value = await read();
        check();
        return value;
      } catch (error) {
        const code = (error as { code?: string }).code?.replace(
          "firestore/",
          ""
        );
        if (
          attempt >= RETRY_DELAYS_MS.length ||
          !["unavailable", "deadline-exceeded", "aborted", "internal"].includes(
            code || ""
          )
        )
          throw error;
        await pauseForRetry(attempt);
      }
    }
  };
  const stopWatching = useAuthStore.subscribe(() => {
    if (
      auth.currentUser?.uid !== user.uid ||
      useAuthStore.getState().accountId !== expectedAccount
    )
      invalidated = true;
  });
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      closed = true;
      reject(new Error("Web setup connection timed out"));
    }, WEB_HANDOFF_TIMEOUT_MS);
  });
  const userRef = getDocument<User>(`User/${user.uid}`);
  const work = async (): Promise<HandoffResult> => {
    check();
    const latest = (
      await readFromServer(() => getDocFromServer(userRef))
    ).data()?.webOnboardingHandoff;
    check();
    // A local acknowledgement means we selected this setup before, not that
    // onboarding finished. Recover its checkpoint when it is still the current
    // account; never undo a deliberate choice to stay on another account.
    if (!candidate)
      candidate = unhandledWebHandoff(user.uid, latest, initialAccountId);
    if (!candidate) return { status: "none" };
    if (
      !options.intent &&
      !sameSession(latest, candidate) &&
      candidate.accountId !== initialAccountId
    )
      return { status: "none" };
    const settingsRef = getDocument<AccountSettings>(
      `Account/${candidate.accountId}/Settings/account`
    );
    stage = "read-settings";
    let settings = (
      await readFromServer(() => getDocFromServer(settingsRef))
    ).data();
    check();
    let origin =
      settings?.webOnboardingHandoff ||
      (sameSession(latest, candidate) ? latest : undefined);
    if (
      !isWebHandoff(origin) ||
      !sameSession(origin, candidate) ||
      (origin.userId && origin.userId !== user.uid)
    )
      return { status: "none" };
    verifiedAccountId = origin.accountId;
    // A verified setup is not the same as "no handoff" when progress is absent
    // or inconsistent. Re-read briefly, then retain a recoverable gate.
    for (
      let attempt = 0;
      !webHandoffIsComplete(origin, settings) &&
      !matchesSetup(origin, settings?.onboardingProgress);
      attempt++
    ) {
      if (attempt >= RETRY_DELAYS_MS.length) {
        console.warn(
          "[webOnboardingHandoff] Verified setup has no matching checkpoint",
          {
            accountId: origin.accountId,
            sessionId: webHandoffId(origin),
            flowType: settings?.onboardingProgress?.flowType,
          }
        );
        return { status: "recovery-required", accountId: origin.accountId };
      }
      await pauseForRetry(attempt);
      settings = (
        await readFromServer(() => getDocFromServer(settingsRef))
      ).data();
      const refreshed = settings?.webOnboardingHandoff;
      if (refreshed) {
        if (
          !isWebHandoff(refreshed) ||
          !sameSession(refreshed, origin) ||
          (refreshed.userId && refreshed.userId !== user.uid)
        )
          throw new CancelledHandoff();
        origin = refreshed;
      }
    }
    let finished = webHandoffIsComplete(origin, settings);
    if (finished && !options.intent) return { status: "none" };
    stage = "check-app-version";
    const supported = await ensureWebHandoffVersion(
      origin.minimumAppVersion,
      isCurrent
    );
    check();
    if (!supported)
      return { status: "update-required", accountId: origin.accountId };

    stage = "wait-for-account-settings";
    expectedAccount = origin.accountId;
    selected = true;
    initial.setAccountId(origin.accountId);
    await waitForState(
      useAuthStore,
      (state) =>
        state.accountSettingsFor === origin.accountId &&
        selectAccountSettingsLoaded(state) &&
        (finished
          ? webHandoffIsComplete(
              accountWebHandoff(
                origin.accountId,
                user.uid,
                state.accountSettings
              ) || origin,
              state.accountSettings
            )
          : matchesSetup(origin, state.accountSettings?.onboardingProgress)),
      WEB_HANDOFF_TIMEOUT_MS
    );
    check();
    stage = "acknowledge-handoff";
    await runTransaction(db, async (transaction) => {
      const latestUser = (await transaction.get(userRef)).data()
        ?.webOnboardingHandoff;
      const currentSettings = (await transaction.get(settingsRef)).data();
      check();
      if (
        !options.intent &&
        !sameSession(latestUser, origin) &&
        origin.accountId !== initialAccountId
      )
        throw new CancelledHandoff();
      const currentOrigin = accountWebHandoff(
        origin.accountId,
        user.uid,
        currentSettings,
        latestUser
      );
      if (!currentOrigin || !sameSession(currentOrigin, origin))
        throw new CancelledHandoff();
      // Another device may have finished while this one was attaching its
      // listener. Never downgrade complete to resumed or reopen its checkpoint.
      finished =
        finished || webHandoffIsComplete(currentOrigin, currentSettings);
      if (
        !finished &&
        !matchesSetup(origin, currentSettings?.onboardingProgress)
      )
        throw new Error("Saved web setup changed while it was being restored");
      const resumed: Handoff = {
        // Preserve the session's completion protocol across mixed app versions.
        // Only an explicit completion can upgrade an unfinished legacy session.
        ...currentOrigin,
        userId: user.uid,
        sessionId: webHandoffId(origin),
        status: finished ? "complete" : "resumed",
      };
      transaction.set(
        settingsRef,
        { webOnboardingHandoff: resumed },
        { merge: true }
      );
      if (sameSession(latestUser, origin))
        transaction.update(userRef, { webOnboardingHandoff: resumed });
    });
    check();
    acknowledgeWebHandoff(user.uid, origin);
    return { status: "resumed", destination: finished ? "/" : "/onboarding" };
  };
  try {
    const result = await Promise.race([work(), deadline]);
    if (
      result.status === "none" &&
      candidate &&
      !options.intent &&
      isCurrent()
    ) {
      acknowledgeWebHandoff(user.uid, candidate);
    }
    return result;
  } catch (error) {
    // Undo only our selection, never a user's subsequent account/identity change.
    if (
      selected &&
      !invalidated &&
      auth.currentUser?.uid === user.uid &&
      useAuthStore.getState().accountId === expectedAccount
    ) {
      expectedAccount = initialAccountId;
      initial.setAccountId(initialAccountId);
    }
    if (error instanceof CancelledHandoff) return { status: "deferred" };
    const code = (error as { code?: string }).code;
    console.warn("[webOnboardingHandoff] Unable to resume saved setup", {
      stage,
      elapsedMs: Date.now() - startedAt,
      code,
      message: error instanceof Error ? error.message : undefined,
    });
    if (candidate && (code === "permission-denied" || code === "not-found")) {
      if (candidate && !options.intent && isCurrent())
        acknowledgeWebHandoff(user.uid, candidate);
      return { status: "none" };
    }
    // Failure to discover a marker is not evidence that a web setup exists.
    // Let ordinary accounts use their usual settings/progress checks. Once a
    // candidate is known, retain the retry gate so its setup cannot be skipped.
    return candidate
      ? {
          status: "retry",
          ...(verifiedAccountId ? { accountId: verifiedAccountId } : {}),
        }
      : { status: "discovery-unavailable" };
  } finally {
    closed = true;
    clearTimeout(timer);
    stopWatching();
  }
}
